import { format } from "date-fns";
import qs from "qs";
import * as Yup from "yup";
import { ClientFilter } from "../clients/ClientFilter";
import httpClient from "../config/HttpClient";
import { proformaInvoiceSchema, getDefaultProformaInvoiceData, ProformaInvoiceData } from "../invoice/ProformaInvoiceModel";
import PageRequest from "../PageRequest";
import PageResponse from "../PageResponse";
import { EmailData, defaultEmailData, emailSchema, EMAIL_URL } from "./EmailModel";

export interface BatchEmailData {
  content: EmailData;
  withInvoice: boolean;
  invoice: ProformaInvoiceData;
}

export interface EmailResults {
  id: string;
  senderName: string;
  sentCount: number;
  failedCount: number;
  status: string;
  finishedAt: string;
  invoiceSent: boolean;
  filter: ClientFilter;
}

export interface EmailRecipient {
  clientId: string;
  email: string;
  status: string;
  createdAt: string;
  errorMessage: string;
  opened: boolean;
}

export const defaultBatchEmailData: Readonly<BatchEmailData> = {
  content: defaultEmailData,
  withInvoice: false,
  invoice: getDefaultProformaInvoiceData(),
};

export const batchEmailSchema = Yup.object().shape({
  content: emailSchema,
  withInvoice: Yup.boolean(),
  invoice: Yup.object().when('withInvoice', ([withInvoice]) => {
    return withInvoice ? proformaInvoiceSchema : Yup.object().nullable();
  }),
});

export class BatchEmailModel implements BatchEmailData {
  content: EmailData;
  withInvoice: boolean;
  invoice: ProformaInvoiceData;

  constructor(data: Partial<BatchEmailData> = {}) {
    const mergedData = { ...defaultBatchEmailData, ...data };
    this.content = { ...mergedData.content };
    this.withInvoice = mergedData.withInvoice;
    this.invoice = { ...mergedData.invoice };
  }

  static createDefault(): BatchEmailModel {
    return new BatchEmailModel();
  }

  static defaultValue(): BatchEmailData {
    return { ...defaultBatchEmailData };
  }

  private static serializeParams(params: Record<string, unknown>): string {
    return qs.stringify(params, {
      serializeDate: (date: Date) => format(date, "yyyy-MM-dd"),
    });
  }

  static async sendBatch(content: BatchEmailData, filter: ClientFilter): Promise<void> {
    try {
      const request = {
        content: content.content,
        invoice: content.withInvoice ? content.invoice : undefined,
        filter: {
          ...filter,
          state: filter.state === "" ? null : filter.state,
        },
      };

      await httpClient.post(`${EMAIL_URL}/send`, request);
    } catch (error) {
      console.error('Failed to send batch email:', error);
      throw error;
    }
  }

  static async sentEmails(page: PageRequest): Promise<PageResponse<EmailResults>> {
    try {
      const response = await httpClient.get<PageResponse<EmailResults>>(EMAIL_URL, {
        params: page,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch sent emails:', error);
      throw error;
    }
  }

  static async emailRecipients(
    resultId: string,
    page: PageRequest,
    filter: Record<string, unknown>
  ): Promise<PageResponse<EmailRecipient>> {
    try {
      const response = await httpClient.get<PageResponse<EmailRecipient>>(
        `${EMAIL_URL}/${resultId}/recipients`,
        {
          params: { ...page, ...filter },
          paramsSerializer: this.serializeParams,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch email recipients:', error);
      throw error;
    }
  }

  static async allEmailRecipients(
    page: PageRequest,
    filter: Record<string, unknown>
  ): Promise<PageResponse<EmailRecipient>> {
    try {
      const response = await httpClient.get<PageResponse<EmailRecipient>>(
        `${EMAIL_URL}/recipients`,
        {
          params: { ...page, ...filter },
          paramsSerializer: this.serializeParams,
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch all email recipients:', error);
      throw error;
    }
  }

  toJSON(): BatchEmailData {
    return {
      content: this.content,
      withInvoice: this.withInvoice,
      invoice: this.invoice,
    };
  }
}

export default BatchEmailModel;
